/**
 * @file lib/antd-app.tsx
 * @description antd 全局静态实例桥接（message / modal / notification）。
 *
 * 背景：
 * antd 6 的静态方法 message.xxx() / Modal.confirm() / notification.xxx()
 * 无法消费 React context（主题、locale、StyleProvider 等），控制台会告警：
 *   "Static function can not consume context like dynamic theme. Please use 'App' component instead."
 *
 * 官方解法是用 `<App>` 组件 + `App.useApp()` 拿到可消费 context 的实例。
 * 但项目里有大量非组件代码（事件回调、mutation onError 等）直接静态调用，
 * 无法在那些位置使用 hook。
 *
 * 本模块采用 antd 官方文档「Global Scene Setup」模式：
 * - 模块级导出 message / modal / notification（ES module live binding）。
 * - `<AntdAppBridge />` 挂在 antd `<App>` 内部，通过 `App.useApp()` 把可消费 context
 *   的实例写入模块级变量；live binding 会让所有 importer 自动拿到最新实例。
 * - 初始值用 antd 静态实例兜底，确保 bridge 挂载前（首帧）调用也不会崩溃。
 *
 * 用法：把原来的 `import { message } from 'antd'` 改为
 *       `import { message } from '@/lib/antd-app'`，调用方式完全不变。
 *
 * 注意：本文件导出的 message/modal/notification 是「可重新赋值的 live binding」，
 * 因此消费侧必须用具名导入（import { message } from ...），不要解构后缓存。
 */

import {
  App as AntdApp,
  message as staticMessage,
  Modal as staticModal,
  notification as staticNotification,
} from 'antd';
import type { MessageInstance } from 'antd/es/message/interface';
import type { ModalStaticFunctions } from 'antd/es/modal/confirm';
import type { NotificationInstance } from 'antd/es/notification/interface';

/**
 * 模块级实例：初始用 antd 静态实例兜底（首帧安全），
 * AntdAppBridge 挂载后替换为可消费 context 的 hook 实例。
 */
let message: MessageInstance = staticMessage;
let modal: Omit<ModalStaticFunctions, 'warn'> = staticModal;
let notification: NotificationInstance = staticNotification;

/**
 * 桥接组件：必须渲染在 antd `<App>` 内部。
 * 在渲染期把 `App.useApp()` 的实例写入模块级变量（幂等，StrictMode 双渲染安全）。
 */
export function AntdAppBridge() {
  const staticFn = AntdApp.useApp();
  message = staticFn.message;
  modal = staticFn.modal;
  notification = staticFn.notification;
  return null;
}

export { message, modal, notification };
